import React, { useState, useEffect } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { getVideos, getChannels, getEditors, updateVideo, createVideo } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Plus, User, CalendarBlank, FilmStrip, YoutubeLogo, TiktokLogo, Clock } from "@phosphor-icons/react";
import { Facebook } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const STAGES = [
  { id: "idea", label: "Idea", color: "#64748B" },
  { id: "script", label: "Script", color: "#D946EF" },
  { id: "filming", label: "Filming", color: "#F59E0B" },
  { id: "editing", label: "Editing", color: "#3B82F6" },
  { id: "review", label: "Review", color: "#F43F5E" },
  { id: "ready", label: "Ready", color: "#10B981" },
  { id: "scheduled", label: "Scheduled", color: "#06B6D4" },
  { id: "published", label: "Published", color: "#22C55E" },
];

const PRIORITIES = ["high", "medium", "low"];

const PlatformIcons = ({ video }) => {
  const platforms = [
    { key: "uploaded_youtube", icon: <YoutubeLogo size={14} weight="fill" />, label: "YouTube", color: "text-red-500" },
    { key: "uploaded_facebook", icon: <Facebook size={14} />, label: "Facebook", color: "text-blue-500" },
    { key: "uploaded_tiktok", icon: <TiktokLogo size={14} weight="fill" />, label: "TikTok", color: "text-white" },
  ];
  return (
    <div className="flex items-center gap-1.5" data-testid={`platform-icons-${video.id}`}>
      {platforms.map((p) => (
        <span
          key={p.key}
          className={`flex items-center justify-center w-5 h-5 rounded ${
            video[p.key]
              ? `${p.color} bg-white/10`
              : "text-gray-600 bg-white/5"
          }`}
          title={`${p.label}: ${video[p.key] ? "Uploaded" : "Pending"}`}
        >
          {p.icon}
        </span>
      ))}
    </div>
  );
};

const VideoCard = ({ video, index, onUpdate }) => {
  const priorityColors = {
    high: "border-l-red-500",
    medium: "border-l-amber-500",
    low: "border-l-emerald-500",
  };

  return (
    <Draggable draggableId={video.id} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          className={`kanban-card ${snapshot.isDragging ? "kanban-card-dragging" : ""} border-l-4 ${priorityColors[video.priority]}`}
          data-testid={`video-card-${video.id}`}
        >
          <div className="flex items-start justify-between mb-2">
            <div className="flex items-center gap-2">
              <div
                className="w-2 h-2 rounded-full flex-shrink-0"
                style={{ backgroundColor: video.channel_color || "#3B82F6" }}
              />
              <span className="text-xs text-gray-500 truncate max-w-[120px]">
                {video.channel_name}
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              {video.short_required && (
                <span className="text-[10px] px-1 py-0.5 rounded bg-amber-500/20 text-amber-400 font-medium" title="Short Required">
                  S
                </span>
              )}
              <div className="flex items-center gap-1">
                {video.is_long && (
                  <span className="text-xs px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-400">L</span>
                )}
                {video.is_short && (
                  <span className="text-xs px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-400">
                    S{video.shorts_count > 0 ? `(${video.shorts_count})` : ""}
                  </span>
                )}
              </div>
            </div>
          </div>
          
          <h4 className="text-sm font-medium text-white mb-3 line-clamp-2">
            {video.title}
          </h4>

          <div className="flex items-center justify-between text-xs text-gray-500">
            <div className="flex items-center gap-1">
              <User size={12} />
              <span>{video.editor_name || "Unassigned"}</span>
            </div>
            <PlatformIcons video={video} />
          </div>
          {video.scheduled_date && (
            <div className="flex items-center gap-1 text-xs text-gray-500 mt-1.5">
              <CalendarBlank size={12} />
              <span>{video.scheduled_date}</span>
            </div>
          )}
        </div>
      )}
    </Draggable>
  );
};

export default function Pipeline() {
  const { user, hasPermission } = useAuth();
  const [videos, setVideos] = useState([]);
  const [channels, setChannels] = useState([]);
  const [editors, setEditors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterChannel, setFilterChannel] = useState("");
  const [filterType, setFilterType] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    channel_id: "",
    type: "long",
    stage: "idea",
    priority: "medium",
  });

  const fetchData = async () => {
    try {
      const [videosRes, channelsRes, editorsRes] = await Promise.all([
        getVideos(),
        getChannels(),
        getEditors(),
      ]);
      setVideos(videosRes.data);
      setChannels(channelsRes.data);
      setEditors(editorsRes.data);
    } catch (err) {
      toast.error("Failed to load pipeline");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDragEnd = async (result) => {
    const { destination, source, draggableId } = result;

    if (!destination) return;
    if (destination.droppableId === source.droppableId && destination.index === source.index) return;

    const newStage = destination.droppableId;
    const video = videos.find((v) => v.id === draggableId);

    if (!video) return;

    // Check editor role restrictions
    if (user?.role === "editor") {
      if (!(video.stage === "editing" && newStage === "review")) {
        toast.error("Editors can only move videos from Editing to Review");
        return;
      }
    }

    // Optimistically update
    const updatedVideos = videos.map((v) =>
      v.id === draggableId ? { ...v, stage: newStage } : v
    );
    setVideos(updatedVideos);

    try {
      await updateVideo(draggableId, { stage: newStage });
      toast.success(`Moved to ${newStage}`);
    } catch (err) {
      toast.error("Failed to update video");
      fetchData(); // Revert
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await createVideo(formData);
      toast.success("Video created");
      setDialogOpen(false);
      setFormData({ title: "", channel_id: "", type: "long", stage: "idea", priority: "medium" });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create video");
    }
  };

  const filteredVideos = videos.filter((video) => {
    if (filterChannel && video.channel_id !== filterChannel) return false;
    if (filterType && video.type !== filterType) return false;
    return true;
  });

  const getVideosByStage = (stage) => filteredVideos.filter((v) => v.stage === stage);

  const canEdit = hasPermission("edit_videos");
  const canCreate = hasPermission("create_videos");

  if (loading) {
    return (
      <div className="flex gap-4 overflow-x-auto pb-4">
        {STAGES.map((stage) => (
          <div key={stage.id} className="kanban-column min-h-[400px]">
            <div className="skeleton h-6 w-20 mb-4" />
            <div className="space-y-3">
              <div className="skeleton h-24 w-full" />
              <div className="skeleton h-24 w-full" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <Select value={filterChannel} onValueChange={(v) => setFilterChannel(v === "all" ? "" : v)}>
            <SelectTrigger className="w-48 bg-white/5 border-white/10 text-white" data-testid="filter-channel">
              <SelectValue placeholder="All Channels" />
            </SelectTrigger>
            <SelectContent className="bg-[#1A1A2E] border-white/10">
              <SelectItem value="all" className="text-white">All Channels</SelectItem>
              {channels.map((c) => (
                <SelectItem key={c.id} value={c.id} className="text-white">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: c.color }} />
                    {c.name}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={filterType} onValueChange={(v) => setFilterType(v === "all" ? "" : v)}>
            <SelectTrigger className="w-32 bg-white/5 border-white/10 text-white" data-testid="filter-type">
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent className="bg-[#1A1A2E] border-white/10">
              <SelectItem value="all" className="text-white">All Types</SelectItem>
              <SelectItem value="long" className="text-white">Long</SelectItem>
              <SelectItem value="short" className="text-white">Short</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {canCreate && (
          <Button onClick={() => setDialogOpen(true)} className="btn-primary" data-testid="create-video-btn">
            <Plus size={18} className="mr-2" />
            New Video
          </Button>
        )}
      </div>

      {/* Kanban Board */}
      <DragDropContext onDragEnd={handleDragEnd}>
        <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-thin">
          {STAGES.map((stage) => (
            <Droppable key={stage.id} droppableId={stage.id}>
              {(provided, snapshot) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className={`kanban-column min-h-[500px] ${snapshot.isDraggingOver ? "bg-white/[0.02] border-blue-500/30" : ""}`}
                  data-testid={`kanban-column-${stage.id}`}
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: stage.color }}
                      />
                      <h3 className="font-medium text-white text-sm">{stage.label}</h3>
                      <span className="text-xs text-gray-500 bg-white/5 px-2 py-0.5 rounded-full">
                        {getVideosByStage(stage.id).length}
                      </span>
                    </div>
                  </div>

                  <div className="space-y-3">
                    {getVideosByStage(stage.id).map((video, index) => (
                      <VideoCard
                        key={video.id}
                        video={video}
                        index={index}
                        onUpdate={fetchData}
                      />
                    ))}
                    {provided.placeholder}
                  </div>

                  {getVideosByStage(stage.id).length === 0 && !snapshot.isDraggingOver && (
                    <div className="flex-1 flex items-center justify-center text-gray-600 text-sm py-8">
                      No videos
                    </div>
                  )}
                </div>
              )}
            </Droppable>
          ))}
        </div>
      </DragDropContext>

      {/* Quick Create Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#1A1A2E] border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Quick Add Video</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-gray-300">Title</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="bg-white/5 border-white/10 text-white"
                placeholder="Video title"
                required
                data-testid="quick-video-title"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-gray-300">Channel</Label>
              <Select value={formData.channel_id} onValueChange={(v) => setFormData({ ...formData, channel_id: v })}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="quick-video-channel">
                  <SelectValue placeholder="Select channel" />
                </SelectTrigger>
                <SelectContent className="bg-[#1A1A2E] border-white/10">
                  {channels.map((c) => (
                    <SelectItem key={c.id} value={c.id} className="text-white">{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-300">Type</Label>
                <Select value={formData.type} onValueChange={(v) => setFormData({ ...formData, type: v })}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1A1A2E] border-white/10">
                    <SelectItem value="long" className="text-white">Long</SelectItem>
                    <SelectItem value="short" className="text-white">Short</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-gray-300">Priority</Label>
                <Select value={formData.priority} onValueChange={(v) => setFormData({ ...formData, priority: v })}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1A1A2E] border-white/10">
                    {PRIORITIES.map((p) => (
                      <SelectItem key={p} value={p} className="text-white capitalize">{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1 btn-secondary">
                Cancel
              </Button>
              <Button type="submit" className="flex-1 btn-primary" data-testid="quick-video-submit">
                Create Video
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
